// Seeded randomness (ENG-3). All sim randomness flows from here — Math.random is
// lint-banned under src/sim. xoshiro128** for the stream, splitmix64 for seeding
// and for forking sub-streams by (parentSeed, saltString). Same seed ⇒ identical
// output on every platform (integer ops only).

const MASK64 = (1n << 64n) - 1n;

/** One splitmix64 step; deterministic 64-bit mix used only for seeding. */
export function splitmix64(x: bigint): bigint {
  let z = (x + 0x9e3779b97f4a7c15n) & MASK64;
  z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK64;
  z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK64;
  return (z ^ (z >> 31n)) & MASK64;
}

/** 64-bit FNV-1a of a string — turns a salt/seed string into a bigint. */
export function hashString(s: string): bigint {
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < s.length; i++) {
    h ^= BigInt(s.charCodeAt(i) & 0xff);
    h = (h * 0x100000001b3n) & MASK64;
  }
  return h;
}

/** Coerce any seed form to a 64-bit value. */
export function seedFromAny(seed: number | bigint | string): bigint {
  if (typeof seed === 'bigint') return seed & MASK64;
  if (typeof seed === 'string') return hashString(seed);
  // Numbers: hash the decimal text so fractional/large seeds are stable.
  return Number.isInteger(seed) ? BigInt(seed) & MASK64 : hashString(String(seed));
}

// ── Salt registry (E-25): every fork salt string is declared once. Declaring the
//    same salt twice is a bug (two subsystems colliding on one identifier). ────
const declaredSalts = new Set<string>();
export function defineSalt(name: string): string {
  if (declaredSalts.has(name)) {
    throw new Error(`salt '${name}' already defined — fork salts must be unique (E-25)`);
  }
  declaredSalts.add(name);
  return name;
}
/** Test-only: clear the salt registry (module state persists across a test file). */
export function _resetSalts(): void {
  declaredSalts.clear();
}

const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;

/** xoshiro128** PRNG. Construct from a seed, `fork(salt)` for independent streams. */
export class Rng {
  private s0 = 0;
  private s1 = 0;
  private s2 = 0;
  private s3 = 0;
  private readonly seed: bigint;

  constructor(seed: number | bigint | string) {
    this.seed = seedFromAny(seed);
    // Seed the 128-bit state from two splitmix64 outputs.
    const a = splitmix64(this.seed);
    const b = splitmix64(a);
    this.s0 = Number(a & 0xffffffffn) >>> 0;
    this.s1 = Number((a >> 32n) & 0xffffffffn) >>> 0;
    this.s2 = Number(b & 0xffffffffn) >>> 0;
    this.s3 = Number((b >> 32n) & 0xffffffffn) >>> 0;
    // Avoid the all-zero state.
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 1;
  }

  /** Next 32-bit unsigned integer. */
  nextU32(): number {
    const result = (Math.imul(rotl(Math.imul(this.s1, 5) >>> 0, 7), 9)) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;
    this.s2 ^= t;
    this.s3 = rotl(this.s3, 11);
    return result;
  }

  /** Float in [0, 1). */
  nextFloat(): number {
    return this.nextU32() / 4294967296;
  }

  /** Integer in [0, n). */
  nextInt(n: number): number {
    return Math.floor(this.nextFloat() * n);
  }

  /** A new independent stream derived from (this seed, salt). */
  fork(salt: string): Rng {
    return new Rng(splitmix64(this.seed ^ hashString(salt)));
  }

  /** A keyed stream — deterministic per (seed, salt, ...keys), for any number of integer
   *  keys (an entity id; two parent ids and a tick; a packed cell). Like `fork` it reads
   *  only the seed, so deriving one never advances THIS stream (§28.4: a rule-off world
   *  must consume exactly the draws it always did). Each key is folded through its own
   *  splitmix64 round, so (1, 2) and (2, 1) are different streams. */
  derive(salt: string, ...keys: number[]): Rng {
    let h = splitmix64((this.seed ^ hashString(salt)) & MASK64);
    for (const k of keys) h = splitmix64((h ^ BigInt.asUintN(64, BigInt(Math.trunc(k)))) & MASK64);
    return new Rng(h);
  }

  /** A positional stream — deterministic per (seed, salt, x, z) for worldgen. */
  forkAt(salt: string, x: number, z: number): Rng {
    const mixed = splitmix64(
      (this.seed ^ hashString(salt) ^ ((BigInt(x | 0) & 0xffffffffn) << 32n) ^ (BigInt(z | 0) & 0xffffffffn)) & MASK64,
    );
    return new Rng(mixed);
  }
}

/** `derive` from any seed form (ARCH §28.4's `forkRng(worldSeed, salt, ...keys)`): the
 *  world seed, or an `Rng` whose seed stands for it. Never draws from `base`. */
export function forkRng(base: Rng | number | bigint | string, salt: string, ...keys: number[]): Rng {
  return (base instanceof Rng ? base : new Rng(base)).derive(salt, ...keys);
}

// Heredity's three salts (ARCH §28.14 "Seams"), declared once here per E-25.
/** A genome rolled at spawn / load / toggle, keyed by entity id (or a packed cell). */
export const SALT_HER_ROLL = defineSalt('her:roll');
/** A child recombined at birth, keyed by (min id, max id, tick). */
export const SALT_HER_BREED = defineSalt('her:breed');
/** A harvested seed's pollen mate and recombination, keyed by (cell, tick). */
export const SALT_HER_POLLEN = defineSalt('her:pollen');
